import {ApolloQueryResult} from '@apollo/client';
import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React from 'react';

import {ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT} from 'auth/DefaultSchemaCollaboratorsConfiguration';
import * as BenchlingUtil from 'BenchlingUtil';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import NoOwnersSchemaMessage from 'schemas/NoOwnersSchemaMessage';
import {
  schemaFieldsetName,
  schemaFieldsetNamePluralized,
} from 'schemas/schema-interface/util/schemaFieldsetName';
import SchemaSettingsExportDropdown from 'schemas/SchemaSettingsExportDropdown';
import {getSchemaOwnerChoices} from 'schemas/spec/SchemaSpecUtil';
import SettingsPageContainer from 'settings/SettingsPageContainer';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {capwords} from 'util/NameUtil';

import {SchemaPermissionsGraphQL} from '../../../graphql-fragments/schemas/__generated/SchemaPermissionsGraphQL';
import {SchemaInterfaceEditorQuery} from './__generated/SchemaInterfaceEditorQuery';
import {SchemaInterfaceQuery} from './__generated/SchemaInterfaceQuery';
import ExistingSchemaInterfaceEditor from './ExistingSchemaInterfaceEditor';
import {SchemaInterface} from './SchemaInterfaceConstants';
import SchemaInterfaceCreateFlow from './SchemaInterfaceCreateFlow';
import {schemaInterfaceURL} from './SchemaInterfaceDAO';

type Organization = NonNullable<SchemaInterfaceEditorQuery['currentTenantOrganizations']>[number];

export interface SchemaInterfaceEditorProps {
  interfaceId?: string;
}

export interface SchemaInterfaceEditorQueriedProps {
  schemaPermissions: SchemaPermissionsGraphQL | null;
  organizations: Array<Organization>;
  registryId?: string;
  interface: SchemaInterface | null;
  refetch: () => Promise<ApolloQueryResult<SchemaInterfaceQuery>>;
}

export function SchemaInterfaceEditor(
  props: SchemaInterfaceEditorProps & SchemaInterfaceEditorQueriedProps
): JSX.Element {
  const ownerChoices = getSchemaOwnerChoices();

  if (!props.interfaceId && ownerChoices.length === 0) {
    return <NoOwnersSchemaMessage pluralName={schemaFieldsetNamePluralized()} />;
  }
  return (
    <SettingsPageContainer
      title={capwords(schemaFieldsetNamePluralized())}
      subtitle={props.interface ? props.interface.name : `Create ${capwords(schemaFieldsetName())}`}
      onClickTitle={(): void => {
        BenchlingUtil.navigate(null, schemaInterfaceURL(''));
      }}
      toolbar={
        props.interface && (
          <SchemaSettingsExportDropdown schemaId={props.interface.id} showAuditLogExport={true} />
        )
      }
    >
      {props.interface ? (
        <ExistingSchemaInterfaceEditor
          interfaceId={props.interfaceId}
          interface={props.interface}
          ownerChoices={ownerChoices}
          schemaPermissions={props.schemaPermissions}
          registryId={props.registryId}
          refetch={props.refetch}
        />
      ) : (
        <SchemaInterfaceCreateFlow ownerChoices={ownerChoices} organizations={props.organizations} />
      )}
    </SettingsPageContainer>
  );
}

export const SCHEMA_INTERFACE_EDITOR_QUERY: DocumentNode = gql`
  query SchemaInterfaceEditorQuery @skipAudit @engComponent(name: SCHEMAS) {
    currentTenantOrganizations {
      ... on OrganizationNode {
        ...OrganizationDefaultSchemaCollaboratorsConfiguration
      }
    }
    registries {
      ... on RegistryNode {
        id
        organization {
          id
        }
      }
    }
  }
  ${ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT}
`;

export const SCHEMA_INTERFACE_QUERY: DocumentNode = gql`
  query SchemaInterfaceQuery($id: StrictString!) @skipAudit @engComponent(name: SCHEMAS) {
    schemaInterface(id: $id) {
      ... on SchemaInterfaceNode {
        id
        name
        organization {
          id
          name
          urlIdentifier
        }
        sqlIdentifier
        ... on PoliceableInterface {
          id
          permissions {
            ...SchemaPermissionsGraphQL
          }
        }
        schemas {
          ...TagSchemaGraphQL
        }
        fields {
          id
          apiId
          systemName
          displayName
          fieldType
          isRequired
          isMulti
          isComputed
          schemaFieldSelectorId
          requiredLinkJson
          unit {
            id
            symbol
          }
        }
        itemType
        constraint {
          id
          fieldsJson
          status
          uniqueResidues
          areUniqueResiduesCaseSensitive
          uniqueCanonicalSmiles
          typeSpecificConstraintConfig {
            constraintType
          }
          source {
            id
            name
          }
        }
        labelingStrategies
        prefix
        schemaDescription
        nameTemplates {
          linkedItemId
          templateType
          shouldOrderPartsBySequence
          nameTemplateParts {
            datetimeFormat
            fieldId
            text
            type
          }
        }
      }
      ...HasArchiveRecordGraphQL
    }
  }
  ${getFragmentWrapper('TagSchema').fragment}
  ${getFragmentWrapper('SchemaPermissions').fragment}
  ${getFragmentWrapper('HasArchiveRecord').fragment}
`;

function QueryingSchemaInterfaceEditor(props: SchemaInterfaceEditorProps): JSX.Element | null {
  const queryResult = useBenchlingQuery<SchemaInterfaceEditorQuery>(SCHEMA_INTERFACE_EDITOR_QUERY);
  const schemaInterfaceResult = useSkippableBenchlingQuery<SchemaInterfaceQuery>(SCHEMA_INTERFACE_QUERY, {
    variables: {id: props.interfaceId},
    skip: props.interfaceId == null,
    renderCachedDataWhenRefetching: true,
    fetchPolicy: 'cache-and-network',
  });
  if (
    schemaInterfaceResult.type === QueryResultType.PENDING ||
    queryResult.type === QueryResultType.PENDING
  ) {
    return <CenteredHorizontalThrobber />;
  } else if (
    schemaInterfaceResult.type === QueryResultType.ERROR ||
    queryResult.type === QueryResultType.ERROR
  ) {
    notifications.error(`Could not load ${schemaFieldsetName()}; try reloading the page.`);
    return null;
  }

  let schemaInterface: SchemaInterface | null = null;
  if (schemaInterfaceResult.type === QueryResultType.SUCCESS) {
    const schemaInterfaceOrInaccessible = schemaInterfaceResult.data.schemaInterface;
    if (!isInaccessible(schemaInterfaceOrInaccessible)) {
      schemaInterface = schemaInterfaceOrInaccessible;
    }
  }

  const organizations = queryResult.data.currentTenantOrganizations;

  const orgId = schemaInterface?.organization?.id;
  const registryId = queryResult.data.registries.find((r) => r.organization?.id === orgId)?.id;

  return (
    <SchemaInterfaceEditor
      interfaceId={props.interfaceId}
      interface={schemaInterface}
      schemaPermissions={schemaInterface?.permissions || null}
      organizations={organizations}
      registryId={registryId}
      refetch={() => schemaInterfaceResult.refetch({id: props.interfaceId})}
    />
  );
}

export default QueryingSchemaInterfaceEditor;
